import { Component } from '@theme/component';

import { serviceReady, stockEndpoint } from './admin-v2-customer-services.js';

const endpoint = stockEndpoint;

class AdminV2StockModal extends Component {
  #dialog;
  #lifecycle;
  #request;
  #token;
  #openingButton;
  #busy = false;

  /** @param {MouseEvent} event */
  async open(event) {
    event.preventDefault();
    event.stopPropagation();
    if (this.#dialog?.open || !(await serviceReady('stock', true)) || !this.isConnected) return;
    if (this.#dialog?.open) return;
    this.#openingButton = this.querySelector('button');
    this.#lifecycle = new AbortController();
    this.#token = null;
    this.#dialog = this.querySelector('template').content.firstElementChild.cloneNode(true);
    document.body.append(this.#dialog);
    const options = { signal: this.#lifecycle.signal };
    for (const button of this.#dialog.querySelectorAll('[data-close]')) {
      button.addEventListener('click', () => this.#dialog.close(), options);
    }
    this.#dialog.addEventListener('close', () => this.#cleanup(), options);
    this.#dialog.addEventListener('click', (click) => {
      if (click.target !== this.#dialog) return;
      const rect = this.#dialog.getBoundingClientRect();
      if (click.clientX < rect.left || click.clientX > rect.right || click.clientY < rect.top || click.clientY > rect.bottom) this.#dialog.close();
    }, options);
    this.#dialog.querySelector('form').addEventListener('submit', this.#submit, options);
    this.#dialog.showModal();
    this.#dialog.querySelector('[name="email"]').focus({ preventScroll: true });
  }

  disconnectedCallback() {
    this.#cleanup();
    super.disconnectedCallback();
  }

  #cleanup() {
    this.#request?.abort();
    this.#lifecycle?.abort();
    this.#dialog?.remove();
    this.#dialog = null;
    this.#busy = false;
    this.#token = null;
    if (this.#openingButton?.isConnected) this.#openingButton.focus({ preventScroll: true });
  }

  async #json(url, options) {
    const response = await fetch(url, { ...options, credentials: 'omit', mode: 'cors', redirect: 'error', signal: this.#request.signal });
    const data = await response.json();
    if (!response.ok) {
      if (response.status === 419) this.#token = null;
      throw new Error(Object.values(data.errors || {}).flat()[0] || data.message || this.dataset.error);
    }
    return data;
  }

  #submit = async (event) => {
    event.preventDefault();
    if (this.#busy) return;
    const dialog = this.#dialog;
    const form = dialog.querySelector('form');
    if (!form.reportValidity()) return;
    const values = new FormData(form);
    const quantity = Number(values.get('quantity'));
    this.#busy = true;
    form.querySelector('[type="submit"]').disabled = true;
    form.setAttribute('aria-busy', 'true');
    dialog.querySelector('[data-error]').hidden = true;
    this.#request = new AbortController();
    try {
      if (!(await serviceReady('stock', true))) throw new Error(this.dataset.error);
      if (values.get('consent') !== 'on') throw new Error(this.dataset.error);
      if (!this.#token) {
        this.#token = await this.#json(endpoint + '?variant=' + encodeURIComponent(this.dataset.variantId), { headers: { Accept: 'application/json' } });
      }
      if (this.#token.variant_id !== 'gid://shopify/ProductVariant/' + this.dataset.variantId
        || this.#token.product_id !== 'gid://shopify/Product/' + this.dataset.productId
        || this.#token.consent_version !== 'availability-v1') {
        this.#token = null;
        throw new Error(this.dataset.error);
      }
      const result = await this.#json(endpoint, {
        method: 'POST', headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: values.get('email'), quantity, website: values.get('website'),
          variant_id: this.#token.variant_id, product_id: this.#token.product_id,
          submission_token: this.#token.submission_token, consent: true, consent_version: 'availability-v1',
        }),
      });
      if (result.success !== true || !result.receipt || result.receipt.quantity !== quantity || typeof result.receipt.title !== 'string') throw new Error(this.dataset.error);
      if (this.#dialog !== dialog) return;
      form.hidden = true;
      dialog.querySelector('[data-product]').textContent = result.receipt.title;
      dialog.querySelector('[data-success-text]').textContent = this.dataset.success.replace('[quantity]', String(result.receipt.quantity));
      dialog.querySelector('[data-success]').hidden = false;
      dialog.querySelector('[data-success] button').focus({ preventScroll: true });
    } catch (error) {
      if (error.name === 'AbortError' || this.#dialog !== dialog) return;
      const notice = dialog.querySelector('[data-error]');
      notice.textContent = error instanceof TypeError ? this.dataset.error : error.message;
      notice.hidden = false;
    } finally {
      if (this.#dialog === dialog) {
        this.#busy = false;
        form.querySelector('[type="submit"]').disabled = false;
        form.removeAttribute('aria-busy');
      }
    }
  };
}

if (!customElements.get('admin-v2-stock-modal')) {
  customElements.define('admin-v2-stock-modal', AdminV2StockModal);
}
